import React, { useMemo } from 'react';
import parseColor from './util/parseColor';
import { styled } from './styled';
import { Box } from './Box';

const PaddingBox = styled(Box, {
  shouldForwardProp: (p) => !['variant', 'color', 'fontSize'].includes(p),
})(({
  theme,
  variant,
  color,
  fontSize,
}) => ({
  fontSize: {
    small: '1.25rem',
    medium: '1.5rem',
    large: '2.1875rem',
  }[fontSize] || fontSize,
  borderRadius: '50%',
  width: '1em',
  height: '1em',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  ...({
    outlined: {
      border: '1px solid',
      color: parseColor(theme, color || 'currentColor', 'main'),
    },
    contained: {
      border: 'none',
      color: '#fff',
      backgroundColor: parseColor(theme, color || 'secondary', 'main'),
    },
  }[variant]),
}));

// Extra wrapper to size svg
// because Safari is not smart enough to handle fontSize: inherit on svg
const SizeBox = styled(Box, {
  shouldForwardProp: (p) => p !== 'shrinkFactor',
})(({ shrinkFactor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'inherit',
  width: '1em',
  height: '1em',
  overflow: 'visible',
  transform: `scale(calc(1 - (${shrinkFactor})))`,
  '&>*:not(svg)': {
    transform: `scale(calc(1 / (1 - (${shrinkFactor}))))`,
  },
}));

const iconStyles = (() => ({
  display: 'block',
  color: 'currentColor',
  width: '100% !important',
  height: '100% !important',
}));

const CircleIcon = React.forwardRef((props, ref) => {
  const {
    Icon,
    color,
    variant = 'outlined',
    fontSize = 'medium',
    shrinkFactor = '1 / 3', // css computation
    sx = {},
    className,
    ...iconProps
  } = props;

  const StyledIcon = useMemo(() => styled(Icon)(iconStyles), [Icon]);

  return (
    <PaddingBox
      variant={variant}
      color={color}
      fontSize={fontSize}
      sx={sx}
      className={className}
    >
      <SizeBox shrinkFactor={shrinkFactor}>
        <StyledIcon
          ref={ref}
          {...iconProps}
        />
      </SizeBox>
    </PaddingBox>
  );
});

export default CircleIcon;
